/**
 * Other expenses on a vendor bill (owner, 29 Oct): freight, wall rent ... —
 * heads the administrator keeps on the lookup master (VENDOR_EXPENSE_TYPE),
 * billable on every job of a work order that allows other expenses, and taxed
 * as the head's classification on Company Tax Configuration says.
 * Mirrors backend/src/common/expense-heads.ts and the M13 invoice calculation.
 */
export interface ExpenseHead {
  readonly expenseTypeId: string;
  readonly code: string;
  readonly name: string;
}

/** An other expense as the vendor enters it on the calculation. */
export interface ExpenseEntry {
  readonly expenseTypeId: string;
  readonly amount: number;
  readonly remarks?: string | null;
}

/**
 * POST /v1/vendor/billing/completed-jobs/invoice/calculation — what a bill is
 * generated from: the work at its agreed rate and, when its work order allows
 * them, the expense heads and the expenses already saved.
 */
export interface InvoiceCalculation {
  readonly work: {
    readonly jobRef: string;
    readonly description: string;
    readonly quantity: number;
    readonly uomCode: string;
    readonly rate: number;
    /** Before tax: quantity × rate. */
    readonly amount: number;
  };
  readonly workOrder: { readonly number: string; readonly otherExpensesAllowed: boolean };
  readonly expenseHeads: readonly ExpenseHead[];
  readonly expenses: readonly ExpenseEntry[];
}

/** The calculation's total before GST: the work and every expense entered. */
export function calculationTotal(
  calc: InvoiceCalculation,
  entries: readonly ExpenseEntry[],
): number {
  return entries.reduce((s, e) => s + (Number(e.amount) || 0), calc.work.amount);
}

/** An expense row as typed on the calculation, before it is checked. */
export interface ExpenseRow {
  readonly expenseTypeId: string;
  readonly amount: string;
  readonly remarks: string;
}

export const BLANK_EXPENSE_ROW: ExpenseRow = { expenseTypeId: '', amount: '', remarks: '' };

/** The rows a calculation opens with: the expenses saved on the bill, if any. */
export const expenseRowsOf = (calc: InvoiceCalculation): ExpenseRow[] =>
  calc.expenses.map((e) => ({ expenseTypeId: e.expenseTypeId, amount: String(e.amount), remarks: e.remarks ?? '' }));

/** A row is billable once it names a head and an amount above zero. */
export const expenseRowComplete = (r: ExpenseRow): boolean => r.expenseTypeId !== '' && Number(r.amount) > 0;

/** The rows as the invoice request sends them. */
export const expenseEntriesOf = (rows: readonly ExpenseRow[]): ExpenseEntry[] =>
  rows.map((r) => ({ expenseTypeId: r.expenseTypeId, amount: Number(r.amount), remarks: r.remarks.trim() || undefined }));
